import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import type { QuestionResult } from '../../hooks/useQuizSession'
import { STAGE, didGrow, heroStage, nextReviewLabel, overallNextReviewLabel } from '../../lib/sessionResult'
import ContinueOnIphone from '../ContinueOnIphone'
import EvidenceCard from '../EvidenceCard'
import { Eyebrow, Plant } from '../ui'

// Web has no i18n; mobile carries the translations.
const FIRST_SESSION_PLANTED = 'You planted your first memory'

const MILESTONE_TITLES: Record<string, string> = {
  first_mastered: 'First Memory Locked',
  mastered_10: '10 Mastered',
  mastered_50: '50 Mastered',
  mastered_100: '100 Mastered',
  first_book_mastered: 'Book Mastered',
  accuracy_75: '75% Accuracy',
  sessions_10: '10 Sessions',
  perfect_session: 'Perfect Session',
}
const MILESTONE_MESSAGES: Record<string, string> = {
  first_mastered: "Your first memory is locked in.",
  mastered_10: "10 things you'll never forget.",
  mastered_50: '50 questions. Permanently yours.',
  mastered_100: "100 mastered. That's a real library.",
  first_book_mastered: 'Your first book is completely mastered.',
  accuracy_75: '3 in 4 — your recall is strong.',
  sessions_10: "You've shown up 10 times when it mattered.",
  perfect_session: 'Flawless session.',
}

function ConceptResultCard({ r }: { r: QuestionResult }) {
  const grew = didGrow(r)
  const after = r.afterStage ?? r.beforeStage
  return (
    <Card elevation={0} variant="outlined">
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.75, py: '14px !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <Plant stage={STAGE[r.beforeStage]} size={22} soil={false} dim />
          <Typography component="span" sx={{ mx: 0.75, color: 'text.secondary' }}>→</Typography>
          <Plant stage={STAGE[after]} size={28} soil={false} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle2" sx={{
            fontWeight: 600, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          }}>
            {r.specificTopic || r.questionText}
          </Typography>
          <Box sx={{ mt: 0.5 }}>
            <Eyebrow>{grew ? 'Grew' : 'Held'} · {nextReviewLabel(r.nextReviewInDays)}</Eyebrow>
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}

function MilestoneCard({ milestone }: { milestone: string }) {
  const title = MILESTONE_TITLES[milestone] ?? 'Achievement'
  const message = MILESTONE_MESSAGES[milestone] ?? ''
  return (
    <Card elevation={0} sx={{ bgcolor: 'var(--moss-soft)', border: '1px solid var(--moss-line)' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: '16px !important' }}>
        <Typography sx={{ fontSize: 28, lineHeight: 1 }}>🏆</Typography>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{title}</Typography>
          {message && <Typography variant="body2" color="text.secondary">{message}</Typography>}
        </Box>
      </CardContent>
    </Card>
  )
}

export function SessionCompleteContent({
  totalCount, nextReviewInHours, questionResults, newMilestones, dailyGoalMet, isFirstSession, onDone,
}: {
  totalCount: number
  nextReviewInHours: number | null
  questionResults: QuestionResult[]
  newMilestones: string[]
  dailyGoalMet: boolean
  isFirstSession: boolean
  onDone: () => void
}) {
  const anyGrew = questionResults.some(didGrow)
  const titleAccent = totalCount === 0 ? 'all done.' : anyGrew ? 'grew.' : 'held.'
  const subtitle = totalCount === 0
    ? 'Nothing due right now. Come back when your next review is ready.'
    : anyGrew
      ? 'Every review helps a memory take root — even the answers you missed move it forward.'
      : "These held steady. You'll see them again right when it counts."

  const featured = questionResults
    .filter(r => r.sourceQuote)
    .reduce<QuestionResult | null>((min, r) => (min === null || r.score < min.score ? r : min), null)

  const groups = questionResults.reduce<Record<string, QuestionResult[]>>((acc, r) => {
    (acc[r.bookTitle] ??= []).push(r)
    return acc
  }, {})
  const showBookHeaders = Object.keys(groups).length > 1

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto' }}>
      {isFirstSession ? (
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Plant stage={heroStage(questionResults)} size={72} soil={false} />
          <Typography sx={{
            mt: 2, fontFamily: 'var(--font-serif)', fontWeight: 500,
            fontSize: 'clamp(24px,3.5vw,32px)', letterSpacing: '-0.5px', lineHeight: 1.2,
          }}>
            {FIRST_SESSION_PLANTED}
          </Typography>
          <ContinueOnIphone placement="session_end" />
        </Box>
      ) : (
        <>
          <Box sx={{ mb: 1 }}><Eyebrow moss>{`Reviewed · ${totalCount} ${totalCount === 1 ? 'concept' : 'concepts'}`}</Eyebrow></Box>
          <Typography sx={{ fontFamily: 'var(--font-serif)', fontWeight: 500, fontSize: 'clamp(28px,4vw,38px)', letterSpacing: '-0.6px', lineHeight: 1.1 }}>
            They <em style={{ color: 'var(--moss)' }}>{titleAccent}</em>
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1.5, mb: 3 }}>{subtitle}</Typography>
        </>
      )}

      {Object.entries(groups).map(([book, results]) => (
        <Box key={book} sx={{ mb: 1.5 }}>
          {showBookHeaders && <Box sx={{ mb: 1 }}><Eyebrow moss>{book}</Eyebrow></Box>}
          <Stack spacing={1.25}>
            {results.map((r, i) => <ConceptResultCard key={i} r={r} />)}
          </Stack>
        </Box>
      ))}

      {featured?.sourceQuote && (
        <EvidenceCard sourceQuote={featured.sourceQuote} sourceHighlight={featured.sourceHighlight} />
      )}

      {newMilestones.length > 0 && (
        <Stack spacing={1.25} sx={{ mt: 3 }}>
          {newMilestones.map(m => <MilestoneCard key={m} milestone={m} />)}
        </Stack>
      )}

      <Button variant="contained" size="large" onClick={onDone} fullWidth sx={{ mt: 4 }}>Done</Button>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1.5 }}>
        Next review · {overallNextReviewLabel(nextReviewInHours, dailyGoalMet)}
      </Typography>
    </Box>
  )
}
